import { Inject, Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { filter, take, switchMap, catchError } from 'rxjs/operators';

import { AuthHandler } from '../auth/auth.handler';
import { Env } from '@usimweb/core/models';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  private authEndpoint: string = '';
  private refreshEndpoint: string = '';

  private refreshingInProgress = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(@Inject('environment') private environment: Env, private autHandler: AuthHandler) {
    this.authEndpoint = this.environment.USIM_HOST + '/api/appstore_auth/';
    this.refreshEndpoint = this.environment.USIM_HOST + '/api/token/refresh/';
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // // add authorization header with jwt token if available
    if (this.environment.deployment !== 'appstore') {
      const token = this.autHandler.accessToken;
      if (token) {
        request = request.clone({
          setHeaders: {
            Authorization: `Bearer ${token}`,
          },
        });
      }
      return next.handle(request);
    } else {
      // if deployment is appstore
      if (request.url.includes('pptx')) {
        request = request.clone({ responseType: 'arraybuffer' });
        console.log('Changing request response to arraybuffer', request, request.responseType);
      }
      return next.handle(this.addAuth(request)).pipe(
        catchError((error) => {
          if (error.status !== 401 && error.status !== 0) {
            return throwError(error);
          }

          if (this.refreshingInProgress) {
            return this.refreshTokenSubject.pipe(
              filter((result) => result !== null),
              take(1),
              switchMap((token) => {
                return next.handle(this.addAuth(request));
              })
            );
          } else {
            this.refreshingInProgress = true;
            this.refreshTokenSubject.next(null);

            return this.autHandler.refreshToken.pipe(
              switchMap((token) => {
                this.refreshingInProgress = false;
                this.refreshTokenSubject.next(token);

                return next.handle(this.addAuth(request));
              }),
              catchError((authError) => {
                this.refreshingInProgress = false;
                this.autHandler.forwardToLogin();
                return throwError(authError);
              })
            );
          }
        })
      );
    }
  }

  private addAuth(request: HttpRequest<any>) {
    const token = this.autHandler.accessToken;

    if (
      token === undefined ||
      token === 'undefined' ||
      request.url.includes(this.authEndpoint) ||
      request.url.includes(this.refreshEndpoint)
    ) {
      return request;
    }
    return request.clone({
      setHeaders: {
        Authorization: 'Bearer ' + token,
      },
    });
  }
}
